
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, QrCode, ArrowLeft, Loader2, CheckCircle, AlertOctagon, CreditCard, X, ChevronRight, Copy } from 'lucide-react';
import { Product, User, Wallet as WalletType } from '../types';
import { processPurchase, processCashback } from '../services/storage';

interface CheckoutProps {
  product: Product;
  user: User;
  activeWallet: WalletType;
  onSuccess: (updatedUser: User) => void;
  onBack: () => void;
}

const CHECKOUT_CSS = `
/* From Uiverse.io by Admin12121 - Adapted for React State */ 
.uiverse-container {
  background-color: #121212; /* Dark Theme Match */
  display: flex;
  width: 460px;
  height: 120px;
  position: relative;
  border-radius: 6px;
  transition: 0.3s ease-in-out;
  border: 1px solid #27272a;
  transform: scale(0.9);
}

.uiverse-container.active {
  transform: scale(1);
  width: 460px; /* Keep width consistent or expand if desired */
  border-color: #ff8c00;
  box-shadow: 0 0 20px rgba(255, 140, 0, 0.1);
}

.left-side {
  background-color: #5de2a3;
  width: 130px;
  height: 120px;
  border-radius: 4px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: default;
  transition: 0.3s;
  flex-shrink: 0;
  overflow: hidden;
}

.right-side {
  width: calc(100% - 130px);
  display: flex;
  align-items: center;
  overflow: hidden;
  cursor: default;
  justify-content: space-between;
  white-space: nowrap;
  transition: 0.3s;
}

.arrow {
  width: 20px;
  height: 20px;
  margin-right: 20px;
}

.new {
  font-size: 23px;
  font-family: "JetBrains Mono", monospace;
  font-weight: bold;
  margin-left: 20px;
  color: #ff8c00; /* Orange Accent */
}

.card {
  width: 70px;
  height: 46px;
  background-color: #c7ffbc;
  border-radius: 6px;
  position: absolute;
  display: flex;
  z-index: 10;
  flex-direction: column;
  align-items: center;
  box-shadow: 9px 9px 9px -2px rgba(77, 200, 143, 0.72);
}

.card-line {
  width: 65px;
  height: 13px;
  background-color: #80ea69;
  border-radius: 2px;
  margin-top: 7px;
}

.buttons {
  width: 8px;
  height: 8px;
  background-color: #379e1f;
  box-shadow: 0 -10px 0 0 #26850e, 0 10px 0 0 #56be3e;
  border-radius: 50%;
  margin-top: 5px;
  transform: rotate(90deg);
  margin: 10px 0 0 -30px;
}

/* Animations triggered by .active class */
.uiverse-container.active .card {
  animation: slide-top 1.2s cubic-bezier(0.645, 0.045, 0.355, 1) both;
}

.uiverse-container.active .post {
  animation: slide-post 1s cubic-bezier(0.165, 0.84, 0.44, 1) both;
}

.uiverse-container.active .dollar {
  animation: fade-in-fwd 0.3s 1s backwards;
}

@keyframes slide-top {
  0% { transform: translateY(0); }
  50% { transform: translateY(-70px) rotate(90deg); }
  60% { transform: translateY(-70px) rotate(90deg); }
  100% { transform: translateY(-8px) rotate(90deg); }
}

.post {
  width: 63px;
  height: 75px;
  background-color: #dddde0;
  position: absolute;
  z-index: 11;
  bottom: 10px;
  top: 120px;
  border-radius: 6px;
  overflow: hidden;
}

.post-line {
  width: 47px;
  height: 9px;
  background-color: #545354;
  position: absolute;
  border-radius: 0px 0px 3px 3px;
  right: 8px;
  top: 8px;
}

.post-line:before {
  content: "";
  position: absolute;
  width: 47px;
  height: 9px;
  background-color: #757375;
  top: -8px;
}

.screen {
  width: 47px;
  height: 23px;
  background-color: #ffffff;
  position: absolute;
  top: 22px;
  right: 8px;
  border-radius: 3px;
}

.numbers {
  width: 12px;
  height: 12px;
  background-color: #838183;
  box-shadow: 0 -18px 0 0 #838183, 0 18px 0 0 #838183;
  border-radius: 2px;
  position: absolute;
  transform: rotate(90deg);
  left: 25px;
  top: 52px;
}

.numbers-line2 {
  width: 12px;
  height: 12px;
  background-color: #aaa9ab;
  box-shadow: 0 -18px 0 0 #aaa9ab, 0 18px 0 0 #aaa9ab;
  border-radius: 2px;
  position: absolute;
  transform: rotate(90deg);
  left: 25px;
  top: 68px;
}

@keyframes slide-post {
  50% { transform: translateY(0); }
  100% { transform: translateY(-70px); }
}

.dollar {
  position: absolute;
  font-size: 16px;
  font-family: "JetBrains Mono", monospace;
  width: 100%;
  left: 0;
  top: 0;
  color: #4b953b;
  text-align: center;
  display: flex; 
  align-items: center; 
  justify-content: center;
  height: 100%;
  font-weight: bold;
}

@keyframes fade-in-fwd {
  0% { opacity: 0; transform: translateY(-5px); }
  100% { opacity: 1; transform: translateY(0); }
}

@media (max-width: 640px) {
  .uiverse-container { width: 300px; transform: scale(0.8); }
  .uiverse-container.active { width: 300px; transform: scale(0.8); }
  .right-side { width: calc(100% - 130px); }
  .new { font-size: 16px; margin-left: 10px; }
  .arrow { display: none; }
}
`;

const Checkout: React.FC<CheckoutProps> = ({ product, user, activeWallet, onSuccess, onBack }) => {
  const [stage, setStage] = useState<'idle' | 'confirming' | 'processing' | 'success'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [generatedCode, setGeneratedCode] = useState<string>('');
  const [updatedUser, setUpdatedUser] = useState<User | null>(null);

  // Safe Accessors
  const currentBalance = activeWallet?.balance ?? 0;
  const productPrice = product?.price ?? 0;

  const handleWalletClick = () => {
    if (currentBalance < productPrice) {
      setError(`Insufficient funds in ${activeWallet.name}.`);
      return;
    }
    setError(null);
    setStage('confirming');
  };

  const confirmTransaction = async () => {
    setStage('processing');
    
    // WAIT FOR ANIMATION (2.5 Seconds)
    await new Promise(r => setTimeout(r, 2500));

    try {
        const result = await processPurchase(user.id, product, activeWallet);
        
        if (result && result.user) {
            setUpdatedUser(result.user);
            setGeneratedCode(result.code);
            setStage('success');
            
            // Handle Perk Cashback
            if (activeWallet.type === 'perk') {
                processCashback(user.id, activeWallet.id, productPrice, product.name);
            }

        } else {
            setError('Transaction failed. Network timeout.');
            setStage('idle');
        }
    } catch (err) {
        setError('System error processing transaction.');
        setStage('idle');
    }
  };

  const handleFinalClose = () => {
      if (updatedUser) {
          onSuccess(updatedUser);
      } else {
          onBack();
      }
  };

  const copyCode = () => {
      if (generatedCode) {
          navigator.clipboard.writeText(generatedCode);
      }
  };

  const handleQRPayment = () => {
    alert("DuitNow QR Scanner module is currently offline.");
  };

  return (
    <div className="max-w-3xl mx-auto pt-10 px-4 pb-20 relative">
      <style>{CHECKOUT_CSS}</style>

      {/* Header / Back Button */}
      <button 
        onClick={onBack}
        disabled={stage !== 'idle'}
        className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-8 group p-2 -ml-2 disabled:opacity-50"
      >
        <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
        <span className="font-mono text-sm uppercase tracking-wider">Cancel Transaction</span>
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Product Preview */}
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-zinc-900 to-black border border-zinc-800 rounded-2xl p-6 shadow-neon relative overflow-hidden">
             <div className="absolute top-0 right-0 w-32 h-32 bg-accent-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
             
             <div className="w-full aspect-square bg-zinc-800 rounded-xl overflow-hidden mb-6 border border-zinc-700/50">
               <img src={product.image} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
             </div>
             
             <h2 className="text-2xl font-bold text-white mb-2">{product.name}</h2>
             <div className="flex justify-between items-end border-t border-zinc-800 pt-4 mt-4">
               <span className="text-zinc-500 font-mono text-xs uppercase tracking-wider">Total Amount</span>
               <span className="text-3xl font-bold text-accent-500 font-mono">${productPrice.toFixed(2)}</span>
             </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-white uppercase tracking-wider mb-4 flex items-center gap-2">
            <CreditCard size={20} className="text-accent-500"/> Payment Method
          </h3>

          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-900/20 border border-red-900/50 p-4 rounded-xl flex items-center gap-3 text-red-400 text-sm mb-4"
            >
              <AlertOctagon size={18} />
              {error}
            </motion.div>
          )}

          <button
            onClick={handleWalletClick}
            disabled={stage !== 'idle'}
            className="w-full bg-zinc-900 border border-zinc-800 hover:border-accent-500 hover:bg-zinc-800 p-5 rounded-xl text-left transition-all group relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed min-h-[80px]"
          >
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-accent-500 text-black rounded-lg">
                  <Wallet size={24} />
                </div>
                <div>
                  <p className="font-bold text-white group-hover:text-accent-500 transition-colors">Pay with {activeWallet.name}</p>
                  <p className="text-xs text-zinc-500 font-mono mt-1">
                     Balance: ${currentBalance.toFixed(2)}
                     {activeWallet.type === 'perk' && <span className="text-purple-400 ml-2">✨ Cashback Active</span>}
                  </p>
                </div>
              </div>
            </div>
            <div className="absolute inset-0 bg-accent-500/5 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
          </button>

          <div className="flex items-center gap-4 my-2">
            <div className="h-[1px] bg-zinc-800 flex-1"></div>
            <span className="text-zinc-600 text-[10px] uppercase font-bold">OR</span>
            <div className="h-[1px] bg-zinc-800 flex-1"></div>
          </div>

          <button
            onClick={handleQRPayment}
            disabled={stage !== 'idle'}
            className="w-full bg-white border border-zinc-200 hover:bg-zinc-100 p-5 rounded-xl text-left transition-all group min-h-[80px] disabled:opacity-50"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-pink-600 text-white rounded-lg">
                <QrCode size={24} />
              </div>
              <div>
                <p className="font-bold text-black">DuitNow QR</p>
                <p className="text-xs text-zinc-500 font-mono mt-1">Scan to Pay Instantly</p>
              </div>
            </div>
          </button>

          <div className="mt-6 border-2 border-dashed border-zinc-800 rounded-xl p-8 flex flex-col items-center justify-center text-center bg-zinc-900/30">
             <div className="bg-white p-2 rounded mb-3 opacity-20 grayscale">
                <QrCode size={64} className="text-black" />
             </div>
             <p className="text-zinc-600 text-xs font-mono">SECURE QR GATEWAY</p>
             <p className="text-zinc-700 text-[10px] uppercase tracking-widest mt-1">Waiting for selection...</p>
          </div>
        </div>
      </div>

      {/* --- CONFIRMATION MODAL --- */}
      <AnimatePresence>
        {stage === 'confirming' && (
           <motion.div
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             exit={{ opacity: 0 }}
             className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
           >
             <motion.div 
               initial={{ scale: 0.9, y: 20 }}
               animate={{ scale: 1, y: 0 }}
               exit={{ scale: 0.9, y: 20 }}
               className="bg-bg-card border border-zinc-700 w-full max-w-sm rounded-2xl shadow-neon overflow-hidden"
             >
                <div className="p-4 border-b border-zinc-800 flex justify-between items-center bg-zinc-900">
                    <h3 className="font-bold text-white text-sm uppercase tracking-wider">Confirm Purchase</h3>
                    <button onClick={() => setStage('idle')} className="text-zinc-500 hover:text-white">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                         <div className="w-12 h-12 rounded bg-zinc-800 overflow-hidden shrink-0 border border-zinc-700">
                             <img src={product.image} className="w-full h-full object-cover" alt="" />
                         </div>
                         <div className="overflow-hidden">
                             <p className="text-white font-bold truncate">{product.name}</p>
                             <p className="text-zinc-500 text-xs">{product.category || 'Item'}</p>
                         </div>
                    </div>

                    <div className="space-y-3 font-mono text-sm mb-6">
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Wallet Balance</span>
                            <span className="text-white">${currentBalance.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Payment</span>
                            <span className="text-red-500">-${productPrice.toFixed(2)}</span>
                        </div>
                        <div className="border-t border-zinc-800 pt-3 flex justify-between font-bold">
                            <span className="text-zinc-300">Remaining</span>
                            <span className="text-accent-500">${(currentBalance - productPrice).toFixed(2)}</span>
                        </div>
                        {activeWallet.type === 'perk' && <p className="text-xs text-purple-400 text-center pt-2">✨ Cashback will be applied after purchase</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <button 
                            onClick={() => setStage('idle')}
                            className="py-3 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 font-bold text-xs uppercase"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={confirmTransaction}
                            className="py-3 rounded-lg bg-accent-600 hover:bg-accent-500 text-black font-bold text-xs uppercase shadow-neon"
                        >
                            Confirm
                        </button>
                    </div>
                </div>
             </motion.div>
           </motion.div>
        )}
      </AnimatePresence>

      {/* --- SUCCESS MODAL WITH CODE --- */}
      <AnimatePresence>
        {stage === 'success' && (
           <motion.div
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             exit={{ opacity: 0 }}
             className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl"
           >
             <motion.div 
               initial={{ scale: 0.9, y: 20 }}
               animate={{ scale: 1, y: 0 }}
               className="bg-bg-card border border-accent-500/50 w-full max-w-sm rounded-2xl shadow-neon overflow-hidden"
             >
                <div className="p-8 flex flex-col items-center text-center">
                    <motion.div 
                       initial={{ scale: 0 }} 
                       animate={{ scale: 1 }} 
                       className="w-16 h-16 bg-accent-500 rounded-full flex items-center justify-center mb-4 shadow-[0_0_20px_rgba(255,140,0,0.5)]"
                    >
                       <CheckCircle size={32} className="text-black" />
                    </motion.div>
                    
                    <h2 className="text-2xl font-bold text-white mb-1">Purchase Verified!</h2>
                    <p className="text-zinc-500 text-xs font-mono mb-6">TRANSACTION HASH: CONFIRMED</p>
                    
                    <div className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-6">
                       <p className="text-zinc-500 text-[10px] uppercase font-bold tracking-wider mb-2">Puchi Special Code</p>
                       <div className="flex items-center justify-center gap-2">
                          <code className="text-xl md:text-2xl font-mono font-bold text-white tracking-widest">{generatedCode}</code>
                          <button onClick={copyCode} className="text-accent-500 hover:text-white p-2">
                             <Copy size={16} />
                          </button>
                       </div>
                    </div>
                    
                    <button 
                       onClick={handleFinalClose}
                       className="w-full bg-accent-600 hover:bg-accent-500 text-black font-bold py-3 rounded-lg uppercase tracking-wider shadow-neon transition-all hover:scale-105 active:scale-95"
                    >
                       View Receipt
                    </button>
                </div>
             </motion.div>
           </motion.div>
        )}
      </AnimatePresence>

      {/* --- PROCESSING ANIMATION MODAL --- */}
      <AnimatePresence>
        {stage === 'processing' && (
           <motion.div
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             exit={{ opacity: 0 }}
             className="fixed inset-0 z-[60] flex flex-col items-center justify-center p-4 bg-black/95 backdrop-blur-xl"
           >
              {/* UIVERSE ANIMATION STRUCTURE */}
              <div className="uiverse-container active">
                <div className="left-side">
                    <div className="card">
                        <div className="card-line"></div>
                        <div className="buttons"></div>
                    </div>
                    <div className="post">
                        <div className="post-line"></div>
                        <div className="screen">
                            <div className="dollar">$</div>
                        </div>
                        <div className="numbers"></div>
                        <div className="numbers-line2"></div>
                    </div>
                </div>
                <div className="right-side">
                    <div className="new">PROCESSING...</div>
                    <svg viewBox="0 0 451.846 451.847" height="512" width="512" xmlns="http://www.w3.org/2000/svg" className="arrow">
                        <path fill="#cfcfcf" className="active-path" d="M345.441 248.292L151.154 442.573c-12.359 12.365-32.397 12.365-44.75 0-12.354-12.354-12.354-32.391 0-44.744L278.318 225.92 106.409 54.017c-12.354-12.359-12.354-32.394 0-44.748 12.354-12.359 32.391-12.359 44.75 0l194.287 194.284c6.177 6.18 9.262 14.271 9.262 22.366 0 8.099-3.091 16.196-9.267 22.373z"></path>
                    </svg>
                </div>
              </div>
              
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}
                className="mt-8 text-zinc-500 font-mono text-xs animate-pulse"
              >
                SECURING TRANSACTION ON BLOCKCHAIN...
              </motion.p>
           </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Checkout;
